/**
 * Authentication Service
 * Handles all authentication-related operations with real API calls
 */

import { User } from '../types';
import { useAppStore } from '../store';
import { auth } from '../config/firebase';
import { signInWithCustomToken, createUserWithEmailAndPassword } from 'firebase/auth';
import {
    login as apiLogin,
    register as apiRegister,
    validateEmail as apiValidateEmail,
    getUser as apiGetUser,
    resetPassword as apiResetPassword,
    updateUser as apiUpdateUser,
    updatePassword as apiUpdatePassword,
    deactivate as apiDeactivate,
    logout as apiLogout,
    apiSendRecoveryCode,
    type LoginParams,
    type RegisterParams,
    type ResetPasswordParams,
    type UpdateUserParams,
    type UpdatePasswordParams,
    type DeactivateParams
} from '../api/auth.api';

export const login = async (params: LoginParams): Promise<{ success: boolean; user?: User; token?: string; message?: string }> => {
    try {
        const response = await apiLogin(params);

        if (response.status === 200 && response.data) {
            // Store tokens in localStorage
            if (response.data.jwtToken) {
                localStorage.setItem('jwt', response.data.jwtToken);
            }
            if (response.data.refreshToken) {
                localStorage.setItem('refreshToken', response.data.refreshToken);
            }

            // Authenticate with Firebase using the custom token
            if (response.data.firebaseCustomToken) {
                try {
                    await signInWithCustomToken(auth, response.data.firebaseCustomToken);
                } catch (firebaseErr) {
                    console.error('Firebase custom token sign-in failed:', firebaseErr);
                }
            }

            // Update app store
            const { setUser, setIsLoggedIn, setToken } = useAppStore.getState();
            setUser(response.data);
            setIsLoggedIn(true);
            setToken(response.data.jwtToken || response.data.token);

            return {
                success: true,
                user: response.data,
                token: response.data.jwtToken || response.data.token
            };
        } else {
            return {
                success: false,
                message: response.error || 'Login failed'
            };
        }
    } catch (error) {
        console.error('Login error:', error);
        return {
            success: false,
            message: 'An unexpected error occurred'
        };
    }
};

export const register = async (params: RegisterParams, extras: any): Promise<{ success: boolean; user?: User; token?: string; message?: string }> => {
    try {
        let firebaseUid = params.firebaseUid || '';
        if (!firebaseUid) {
            try {
                // First create the user in Firebase
                const userCred = await createUserWithEmailAndPassword(auth, params.email, params.password);
                firebaseUid = userCred.user.uid;
            } catch (firebaseErr: any) {
                console.error('Firebase registration error:', firebaseErr);
                return {
                    success: false,
                    message: firebaseErr.message || 'Firebase Registration failed'
                };
            }
        }

        const response = await apiRegister({ ...params, firebaseUid } as any, extras);

        if (response.status === 200 && response.data) {
            // Store tokens in localStorage
            if (response.data.accessToken) {
                localStorage.setItem('jwt', response.data.accessToken);
            }
            if (response.data.refreshToken) {
                localStorage.setItem('refreshToken', response.data.refreshToken);
            }

            // Update app store
            const { setUser, setIsLoggedIn, setToken } = useAppStore.getState();
            setUser(response.data.user);
            setIsLoggedIn(true);
            setToken(response.data.accessToken || response.data.token);

            return {
                success: true,
                user: response.data.user,
                token: response.data.accessToken || response.data.token
            };
        } else {
            return {
                success: false,
                message: response.error || 'Registration failed'
            };
        }
    } catch (error) {
        console.error('Registration error:', error);
        return {
            success: false,
            message: 'An unexpected error occurred'
        };
    }
};

export const validateEmail = async (email: string): Promise<{ success: boolean; message?: string }> => {
    try {
        const response = await apiValidateEmail(email);

        if (response.status === 200) {
            return {
                success: true,
                message: 'Email is available'
            };
        } else {
            return {
                success: false,
                message: response.error || 'Email validation failed'
            };
        }
    } catch (error) {
        console.error('Email validation error:', error);
        return {
            success: false,
            message: 'Failed to validate email'
        };
    }
};

export const resetPassword = async (params: ResetPasswordParams): Promise<{ success: boolean; message?: string }> => {
    try {
        const response = await apiResetPassword(params);

        if (response.status === 200) {
            return {
                success: true,
                message: 'Password reset successful'
            };
        } else {
            return {
                success: false,
                message: response.error || 'Password reset failed'
            };
        }
    } catch (error) {
        console.error('Password reset error:', error);
        return {
            success: false,
            message: 'An unexpected error occurred'
        };
    }
};

export const sendRecoveryCode = async (username: string, email: string): Promise<{ success: boolean; message?: string }> => {
    try {
        const response = await apiSendRecoveryCode({ username, email });

        if (response.status === 200) {
            return {
                success: true,
                message: response.data?.message || 'Recovery code sent successfully'
            };
        } else {
            return {
                success: false,
                message: response.error || 'Failed to send recovery code'
            };
        }
    } catch (error) {
        console.error('Send recovery code error:', error);
        return {
            success: false,
            message: 'An unexpected error occurred'
        };
    }
};

export const logout = async (): Promise<void> => {
    try {
        await apiLogout();
        await auth.signOut();
    } catch (e) {
        console.error("Logout API/Firebase call failed:", e);
    }
    // Use app store logout method
    const { logout } = useAppStore.getState();
    logout();
};

export const getUser = async (username: string): Promise<User | null> => {
    try {
        const response = await apiGetUser(username);

        if (response.status === 200 && response.data) {
            return response.data;
        }
        return null;
    } catch (error) {
        console.error('Get user error:', error);
        return null;
    }
};

export const updateUser = async (params: UpdateUserParams): Promise<{ success: boolean; message?: string }> => {
    try {
        const response = await apiUpdateUser(params);

        if (response.status === 200) {
            return {
                success: true,
                message: 'User updated successfully'
            };
        } else {
            return {
                success: false,
                message: response.error || 'User update failed'
            };
        }
    } catch (error) {
        console.error('Update user error:', error);
        return {
            success: false,
            message: 'An unexpected error occurred'
        };
    }
};

export const updatePassword = async (params: UpdatePasswordParams): Promise<{ success: boolean; message?: string }> => {
    try {
        const response = await apiUpdatePassword(params);

        if (response.status === 200) {
            return {
                success: true,
                message: 'Password updated successfully'
            };
        } else {
            return {
                success: false,
                message: response.error || 'Password update failed'
            };
        }
    } catch (error) {
        console.error('Update password error:', error);
        return {
            success: false,
            message: 'An unexpected error occurred'
        };
    }
};

export const deactivate = async (params: DeactivateParams): Promise<{ success: boolean; message?: string }> => {
    try {
        const response = await apiDeactivate(params);

        if (response.status === 200) {
            // Use app store logout method
            const { logout } = useAppStore.getState();
            logout();

            return {
                success: true,
                message: 'Account deactivated successfully'
            };
        } else {
            return {
                success: false,
                message: response.error || 'Account deactivation failed'
            };
        }
    } catch (error) {
        console.error('Deactivate account error:', error);
        return {
            success: false,
            message: 'An unexpected error occurred'
        };
    }
};
